import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { CheckCircleIcon, LockSimpleIcon, MagnifyingGlassIcon, PackageIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { api, apiError, SITE_ORIGIN } from "@/lib/api";

// Standalone card renderer so the "Recently imported" and "All shop
// items" sections share exactly the same visual + interaction logic.
// Module-scoped (not nested in the component) so it has a stable
// identity across renders.
function renderCard(it, selected, toggle) {
  const isSelected = selected.has(it.id);
  const disabled = it.already_in_stream || it.locked_to_other_stream;
  const thumb = it.images?.[0] ? `${SITE_ORIGIN}/api/files/${it.images[0]}` : null;
  const price =
    it.current_bid || it.starting_bid || it.price || it.buy_now_price || 0;
  return (
    <button
      type="button"
      key={it.id}
      onClick={() => !disabled && toggle(it.id)}
      disabled={disabled}
      className={`relative text-left rounded-2xl border transition overflow-hidden ${
        isSelected
          ? "border-[#1D78AB] ring-2 ring-[#1D78AB]/30"
          : disabled
          ? "border-zinc-200 opacity-50 cursor-not-allowed"
          : "border-zinc-200 hover:border-zinc-400 bg-white"
      }`}
      data-testid={`import-shop-item-${it.id}`}
    >
      <div className="aspect-square bg-zinc-100 relative">
        {thumb ? (
          <img src={thumb} alt={it.title} className="h-full w-full object-cover" />
        ) : (
          <PackageIcon size={36} className="absolute inset-0 m-auto text-zinc-300" />
        )}
        {isSelected && (
          <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-[#1D78AB] flex items-center justify-center shadow-md">
            <CheckCircleIcon size={16} weight="fill" className="text-white" />
          </div>
        )}
        {!isSelected && !disabled && (
          <div className="absolute top-2 right-2 pointer-events-none">
            <Checkbox
              checked={false}
              tabIndex={-1}
              aria-hidden="true"
              className="bg-white/90 border-zinc-400 data-[state=checked]:bg-[#1D78AB] data-[state=checked]:border-[#1D78AB]"
            />
          </div>
        )}
        {disabled && (
          <div className="absolute inset-0 bg-zinc-900/40 flex items-center justify-center">
            <span className="inline-flex items-center gap-1 rounded-full bg-white text-zinc-800 text-[10px] font-bold uppercase tracking-wider px-2 py-1">
              <LockSimpleIcon size={11} weight="fill" />
              {it.already_in_stream ? "Already in show" : "In another live show"}
            </span>
          </div>
        )}
      </div>
      <div className="p-2.5">
        <div className="text-xs font-bold text-zinc-900 line-clamp-2 leading-tight">
          {it.title || "Untitled"}
        </div>
        <div className="mt-1 flex items-center justify-between text-[11px]">
          <span className="font-bold text-zinc-900">${Number(price).toFixed(0)}</span>
          <span className="uppercase text-zinc-400 tracking-wider font-semibold">
            {it.type === "auction"
              ? "Auction"
              : it.type === "fixed"
              ? "Buy now"
              : it.type}
          </span>
        </div>
      </div>
    </button>
  );
}

/**
 * Whatnot-style "Import from Shop" sheet. Lets the seller multi-select
 * their existing shop listings and bulk-attach them to the currently-
 * live stream in a single round-trip.
 *
 * The sheet is intentionally tall + scrollable on mobile and a tidy
 * 720px centered dialog on desktop — matches the visual register of
 * the other live-show overlays (Wallet, More, etc.).
 *
 * Props:
 *   open          — boolean controlling visibility
 *   onOpenChange  — setter for open
 *   streamId      — current live stream id (server validates ownership)
 *   onImported    — optional callback fired with the count after a
 *                   successful import, so the parent can refresh
 */
export default function ImportFromShopSheet({ open, onOpenChange, streamId, onImported }) {
  const [items, setItems] = useState([]);
  const [recentIds, setRecentIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  // Always refetch on open so stale `already_in_stream` flags don't
  // mislead the seller (e.g., they just sold a lot).
  useEffect(() => {
    if (!open || !streamId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/me/shop-listings?stream_id=${streamId}`);
        if (!cancelled) {
          setItems(data.items || []);
          setRecentIds(data.recent_import_ids || []);
          setSelected(new Set());
        }
      } catch (e) {
        if (!cancelled) toast.error(apiError(e) || "Could not load your shop");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, streamId]);

  // Reset search + selection when the sheet closes so the next open
  // is clean.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelected(new Set());
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (!q) return true;
      return (
        (i.title || "").toLowerCase().includes(q) ||
        (i.id || "").toLowerCase().includes(q)
      );
    });
  }, [items, search]);

  // Split filtered results into "Recently imported" (the 6 lots the
  // seller most-recently broadcast in OTHER shows) and "All shop
  // items". Hidden when the user is actively searching — search wants
  // a flat list, not two sections.
  const recentSection = useMemo(() => {
    if (search.trim()) return [];
    const idSet = new Set(recentIds);
    const byId = new Map(filtered.map((r) => [r.id, r]));
    return recentIds.map((id) => byId.get(id)).filter(Boolean);
  }, [filtered, recentIds, search]);

  const restSection = useMemo(() => {
    if (search.trim()) return filtered;
    const idSet = new Set(recentSection.map((r) => r.id));
    return filtered.filter((r) => !idSet.has(r.id));
  }, [filtered, recentSection, search]);

  // Eligible-to-import (excludes "already in this stream" since it's a
  // no-op and excludes "locked to other live stream" since the server
  // would reject it anyway).
  const importable = useMemo(
    () => filtered.filter((i) => i.eligible && !i.already_in_stream),
    [filtered]
  );

  const toggle = (id) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAll = () => setSelected(new Set(importable.map((i) => i.id)));
  const clearAll = () => setSelected(new Set());

  const allSelected =
    importable.length > 0 && importable.every((i) => selected.has(i.id));

  const onImport = async () => {
    if (!selected.size) return;
    setBusy(true);
    try {
      // The server caps each call at 50 ids. Chunk locally to be safe.
      const ids = Array.from(selected);
      const chunks = [];
      for (let i = 0; i < ids.length; i += 50) chunks.push(ids.slice(i, i + 50));
      let totalImported = 0;
      let totalFailed = 0;
      const firstError = { message: "" };
      for (const chunk of chunks) {
        const { data } = await api.post(`/streams/${streamId}/import-listings`, {
          listing_ids: chunk,
        });
        totalImported += data.imported || 0;
        const failed = (data.results || []).filter((r) => !r.ok);
        totalFailed += failed.length;
        if (failed.length && !firstError.message) {
          firstError.message = `${failed[0].listing_id?.slice(-6) || "?"}: ${failed[0].error}`;
        }
      }
      if (totalImported > 0) {
        toast.success(
          `Imported ${totalImported} item${totalImported === 1 ? "" : "s"} to the show`,
          totalFailed
            ? { description: `${totalFailed} skipped · ${firstError.message}` }
            : undefined
        );
      } else if (totalFailed) {
        toast.error(`0 imported · ${firstError.message || "Some items couldn't be added"}`);
      }
      if (totalImported > 0) {
        onImported?.(totalImported);
        onOpenChange?.(false);
      }
    } catch (e) {
      toast.error(apiError(e) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[720px] max-h-[88vh] p-0 flex flex-col"
        data-testid="import-from-shop-sheet"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-200">
          <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-xl">
            <DownloadSimpleIcon size={20} weight="fill" className="text-[#1D78AB]" />
            Import from your shop
          </DialogTitle>
          <DialogDescription>
            Multi-select listings to pull into this live show. Viewers see
            them appear instantly.
          </DialogDescription>
          <div className="mt-3 relative">
            <Input
              autoFocus
              placeholder="Search your shop…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-full bg-zinc-100 border-zinc-200 pl-10 h-10 text-sm"
              data-testid="import-shop-search"
            />
            <MagnifyingGlassIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          </div>
          {importable.length > 0 && (
            <div className="mt-3 flex items-center gap-3 text-xs">
              <button
                type="button"
                onClick={allSelected ? clearAll : selectAll}
                className="font-bold text-[#1D78AB] hover:underline"
                data-testid="import-shop-select-all"
              >
                {allSelected ? "Clear selection" : `Select all (${importable.length})`}
              </button>
              <span className="text-zinc-400">·</span>
              <span className="text-zinc-600">
                {selected.size} of {importable.length} selected
              </span>
            </div>
          )}
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto px-4 py-4"
          data-testid="import-shop-grid"
        >
          {loading ? (
            <div className="text-center text-sm text-zinc-500 py-12">
              Loading your shop…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-sm text-zinc-500 py-12">
              <PackageIcon size={32} className="mx-auto mb-2 text-zinc-300" />
              {items.length === 0
                ? "You don't have any active shop listings yet."
                : "No matches for your search."}
            </div>
          ) : (
            <>
              {/* "Recently imported" smart row — the lots this seller
                  pulls into shows again and again, pre-seeded so they
                  don't have to scroll. Hidden during search. */}
              {recentSection.length > 0 && (
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-500">
                      ⚡ Recently imported
                    </span>
                    <span className="text-[10px] text-zinc-400">
                      ({recentSection.length})
                    </span>
                  </div>
                  <div
                    className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                    data-testid="import-shop-recent-grid"
                  >
                    {recentSection.map((it) => renderCard(it, selected, toggle))}
                  </div>
                </div>
              )}
              {restSection.length > 0 && (
                <div>
                  {recentSection.length > 0 && (
                    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-500 mb-2.5">
                      All shop items
                    </div>
                  )}
                  <div
                    className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                    data-testid="import-shop-grid"
                  >
                    {restSection.map((it) => renderCard(it, selected, toggle))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-zinc-200 flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            className="rounded-full"
            data-testid="import-shop-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onImport}
            disabled={busy || selected.size === 0}
            className="rounded-full bg-[#1D78AB] hover:bg-[#165d85] disabled:opacity-50"
            data-testid="import-shop-confirm"
          >
            <DownloadSimpleIcon size={14} weight="bold" className="mr-1.5" />
            {busy
              ? "Importing…"
              : selected.size === 0
              ? "Select to import"
              : `Import ${selected.size} item${selected.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
